// /swiftboard/estimates/:estimateId/* — estimate workspace (header, live KPIs, tabs).
// Layout: header + KPI strip + tabs stay fixed; only the tab body scrolls, so the
// KPI strip is always visible while editing.
import { NavLink, Outlet, useParams } from "react-router-dom";
import { Copy, Lock } from "lucide-react";
import Skeleton from "../../../../../components/ui/Skeleton";
import { useBreadcrumbLabels } from "../../../../../utils/breadcrumb-labels";
import { EstimateWorkspaceProvider, useWorkspace } from "../../hooks/useEstimateWorkspace";
import { WORKSPACE_TABS } from "./workspace-tabs";
import KpiStrip from "../../components/KpiStrip";
import { PrototypeBadge } from "../../components/EstimateBadges";
import { ErrorState } from "../../components/PageStates";
import { Button } from "../../components/ui";
import WorkspaceHeader from "./WorkspaceHeader";

const TAB_LABELS = { boq: "BOQ", bid: "Bid Form", materials: "Material List", "gen-conditions": "Gen Conditions" };

function WorkspaceTabs() {
  return (
    <nav className="flex gap-0 overflow-x-auto border-b border-border" aria-label="Estimate sections">
      {WORKSPACE_TABS.map((t) => (
        <NavLink
          key={t.path}
          to={t.path}
          className={({ isActive }) =>
            `-mb-px whitespace-nowrap border-b-2 px-3.5 py-2 text-[13px] font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-border_strong ${
              isActive ? "border-primary text-primary" : "border-transparent text-text_secondary"
            }`
          }
        >
          {t.label}
        </NavLink>
      ))}
    </nav>
  );
}

function WorkspaceSkeleton() {
  return (
    <div className="flex flex-col gap-3 py-3">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-5 w-96" />
          <Skeleton className="h-3 w-64" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-8 w-24" />
        </div>
      </div>
      <Skeleton.Tiles count={7} className="grid-cols-7" />
      <Skeleton className="h-8 w-full" />
      <Skeleton.Rows rows={6} />
    </div>
  );
}

function WorkspaceLayout() {
  const ws = useWorkspace();
  const { estimate, loading, error, calc, isLocked } = ws;

  useBreadcrumbLabels(
    estimate ? { [estimate.id]: `Job ${estimate.jobId} – ${estimate.jobTitle}`, ...TAB_LABELS } : TAB_LABELS
  );

  if (loading && !estimate) return <WorkspaceSkeleton />;
  if (error) {
    return (
      <div className="py-3">
        <ErrorState error={error} onRetry={ws.reload} title="Could not load this estimate" />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 pb-2 pt-3">
      <WorkspaceHeader />
      <KpiStrip kpis={calc?.kpis} loading={!calc} />
      {isLocked && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-[4px] border border-success_border bg-success_bg px-3 py-2 text-[12.5px] text-success" role="status">
          <span className="flex items-center gap-2 font-medium">
            <Lock size={14} aria-hidden="true" /> Locked – duplicate to revise. This estimate was finalized and is read-only.
          </span>
          <Button size="xs" icon={Copy} onClick={ws.duplicate} disabled={!!ws.busy}>
            Duplicate
          </Button>
        </div>
      )}
      <WorkspaceTabs />
      <div className="min-h-0 flex-1 overflow-y-auto">
        <Outlet />
      </div>
    </div>
  );
}

export default function EstimateWorkspace() {
  const { estimateId } = useParams();
  return (
    <div className="h-full overflow-hidden bg-background">
      <EstimateWorkspaceProvider key={estimateId} estimateId={estimateId}>
        <WorkspaceLayout />
      </EstimateWorkspaceProvider>
      <PrototypeBadge />
    </div>
  );
}
